import {
  PayeeEngagementType,
  WorkLogConvertedResourceType
} from "@wingspanhq/wingspan-api-sdk";

export type WorkLogConvertedResourceLabels = {
  /** "View pay statement" / "View invoice" */
  view: string;
  /** "Cancel pay statement" / "Cancel invoice" */
  cancel: string;
  /** Sentence-case noun for inline copy, e.g. "the unpaid invoice". */
  noun: string;
};

const PAY_STATEMENT_LABELS: WorkLogConvertedResourceLabels = {
  view: "View pay statement",
  cancel: "Cancel pay statement",
  noun: "pay statement"
};

const INVOICE_LABELS: WorkLogConvertedResourceLabels = {
  view: "View invoice",
  cancel: "Cancel invoice",
  noun: "invoice"
};

/**
 * An approved work log converts into different downstream resources depending on
 * the engagement: a W-2 employee work log becomes a **pay statement**, while a
 * 1099 contractor work log still becomes an invoice/payable. The UI copy has to
 * follow that rather than being renamed wholesale — "pay statement" is only the
 * correct term on the employee side.
 *
 * Two sources, because `convertedResourceType` is only set once the work log has
 * actually converted:
 *
 *  - **After approval** it says exactly what was produced, so it wins.
 *  - **Before approval** (draft, in-review — every screen that talks about what
 *    approving *will* do) it is null, and the engagement type is what decides.
 *    Passing it is what keeps a W-2 work log from being described as an invoice
 *    on the approve/reject modals.
 *
 * Falls back to the invoice wording when neither is known.
 */
export const selectorWorkLogConvertedResourceLabels = ({
  convertedResourceType,
  engagementType
}: {
  convertedResourceType?: WorkLogConvertedResourceType | null;
  engagementType?: PayeeEngagementType | null;
}): WorkLogConvertedResourceLabels => {
  if (convertedResourceType) {
    return convertedResourceType === WorkLogConvertedResourceType.PAY_STATEMENT
      ? PAY_STATEMENT_LABELS
      : INVOICE_LABELS;
  }

  // Only `Employee` is the W-2 payroll path here, matching how the payer
  // create-work-log modal already classifies engagements. EOR/AOR are separate
  // product paths and are not assumed to produce pay statements.
  return engagementType === PayeeEngagementType.EMPLOYEE
    ? PAY_STATEMENT_LABELS
    : INVOICE_LABELS;
};
